import { useEffect, useState } from "react";
import api from "../../../../utils/api";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    FormLabel,
    OutlinedInput,
    Typography,
} from "@mui/material";

const initialForm = {
    code: "",
    discount: "",
    expire_at: "",
    max_usage: "",
};

function toDateInputValue(expireAt) {
    if (!expireAt) return "";
    const raw = String(expireAt);
    if (raw.includes("T")) return raw.split("T")[0];
    return raw.slice(0, 10);
}

export default function PromocodeModal({ open, onClose, row = null }) {
    const [form, setForm] = useState(initialForm);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (row) {
            setForm({
                code: row.code || "",
                discount: row.discount != null ? String(row.discount) : "",
                expire_at: toDateInputValue(row.expire_at),
                max_usage: row.max_usage != null ? String(row.max_usage) : "",
            });
        } else {
            setForm(initialForm);
        }
        setSaving(false);
        setErrors({});
    }, [row, open]);

    const handleSubmit = async () => {
        setSaving(true);
        setErrors({});
        try {
            const payload = {
                code: String(form.code || "").trim(),
                discount: Number(form.discount),
                expire_at: form.expire_at,
            };
            const maxUsage = String(form.max_usage || "").trim();
            if (maxUsage !== "") {
                payload.max_usage = Number(maxUsage);
            }
            if (row?.id) {
                await api.put(`admin/promocodes/${row.id}`, payload);
            } else {
                await api.post("admin/promocodes", payload);
            }
            onClose(true);
        } catch (e) {
            if (e.errors) setErrors(e.errors);
            else if (e.response?.data?.errors) setErrors(e.response.data.errors);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
            <DialogTitle>{row ? "Edit promocode" : "Create promocode"}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    {row?.id != null && (
                        <Typography variant="body2" color="text.secondary">
                            Times used: {row.used ?? 0}
                        </Typography>
                    )}
                    <FormControl error={!!errors.code?.[0]}>
                        <FormLabel htmlFor="promocode-code">Code</FormLabel>
                        <OutlinedInput
                            id="promocode-code"
                            value={form.code}
                            onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                            placeholder="SUMMER2026"
                        />
                        {errors.code && <FormHelperText>{errors.code[0]}</FormHelperText>}
                    </FormControl>
                    <FormControl error={!!errors.discount?.[0]}>
                        <FormLabel htmlFor="promocode-discount">Discount (%)</FormLabel>
                        <OutlinedInput
                            id="promocode-discount"
                            type="number"
                            inputProps={{ min: 0, max: 100 }}
                            value={form.discount}
                            onChange={(e) => setForm((p) => ({ ...p, discount: e.target.value }))}
                        />
                        {errors.discount && <FormHelperText>{errors.discount[0]}</FormHelperText>}
                    </FormControl>
                    <FormControl error={!!errors.expire_at?.[0]}>
                        <FormLabel htmlFor="promocode-expire">Expiry date</FormLabel>
                        <OutlinedInput
                            id="promocode-expire"
                            type="date"
                            value={form.expire_at}
                            onChange={(e) => setForm((p) => ({ ...p, expire_at: e.target.value }))}
                        />
                        {errors.expire_at && <FormHelperText>{errors.expire_at[0]}</FormHelperText>}
                    </FormControl>
                    <FormControl error={!!errors.max_usage?.[0]}>
                        <FormLabel htmlFor="promocode-max-usage">Max usage (optional)</FormLabel>
                        <OutlinedInput
                            id="promocode-max-usage"
                            type="number"
                            inputProps={{ min: 1 }}
                            value={form.max_usage}
                            onChange={(e) => setForm((p) => ({ ...p, max_usage: e.target.value }))}
                            placeholder="Unlimited if empty"
                        />
                        {errors.max_usage && <FormHelperText>{errors.max_usage[0]}</FormHelperText>}
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)} disabled={saving}>Cancel</Button>
                <Button variant="contained" sx={{ color: "#fff" }} onClick={handleSubmit} disabled={saving}>
                    {saving ? <CircularProgress size={24} color="inherit" /> : (row ? "Save" : "Create")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
